import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, Loader2, Search, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api";
import { ARTICLE_PARAM, longDay } from "../../lib/guide";
import GuideArticle from "./GuideArticle";

const SECTION_PARAM = "s";
const QUERY_PARAM = "q";

/**
 * The guide as a reader moves through it (19.10.2026): search and the
 * chapters on the left, the article on the right, the article and its
 * section in the address so a link or the (?) opens the right place.
 * The admin and the portal each give it their own `apiBase` and `footer`;
 * `?q=` starts a search — the (?) next to a term in the portal sends it.
 */
export default function GuideBrowser({ apiBase, index, footer, welcome, stickyTop = "lg:top-20",
  placeholder = "Caută: mutare, contract, ore…" }) {
  const [params, setParams] = useSearchParams();
  const current = params.get(ARTICLE_PARAM) || "";
  const anchor = params.get(SECTION_PARAM) || "";
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("idle");
  const [query, setQuery] = useState(() => params.get(QUERY_PARAM) || "");
  const [results, setResults] = useState(null);
  const top = useRef(null);

  const open = (id, section = "") => {
    const next = new URLSearchParams(params);
    if (id) next.set(ARTICLE_PARAM, id); else next.delete(ARTICLE_PARAM);
    if (section) next.set(SECTION_PARAM, section); else next.delete(SECTION_PARAM);
    setParams(next);
  };

  useEffect(() => {
    if (!current) { setArticle(null); setState("idle"); return undefined; }
    let live = true;
    setState("loading");
    api.get(`${apiBase}/articles/${current}`)
      .then((r) => { if (live) { setArticle(r.data); setState("ready"); } })
      .catch(() => { if (live) { setArticle(null); setState("missing"); } });
    return () => { live = false; };
  }, [apiBase, current]);

  useEffect(() => {
    if (!anchor) top.current?.scrollIntoView?.({ block: "start" });
  }, [current, anchor]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.get(`${apiBase}/search`, { params: { q } })
        .then((r) => { if (live) setResults(r.data?.results || []); })
        .catch(() => { if (live) setResults([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [apiBase, query]);

  if (!index) {
    return <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin" /> Se încarcă ghidul…</div>;
  }

  return (
    <div ref={top} className="grid gap-6 lg:grid-cols-[minmax(240px,300px)_minmax(0,1fr)] scroll-mt-20" data-testid="guide">
      <aside className={`grid content-start gap-5 lg:sticky ${stickyTop} lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto`}>
        <label className="relative block">
          <span className="sr-only">Caută în ghid</span>
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder}
            className="w-full rounded-xl border border-[#E5E7EB] bg-white py-2 pl-9 pr-8 text-sm focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20"
            data-testid="guide-search" />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Șterge căutarea"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#9CA3AF] hover:text-[#373A45]">
              <X size={14} />
            </button>
          )}
        </label>

        {results ? (
          <SearchResults results={results} query={query} onOpen={(id, a) => { open(id, a); }} />
        ) : (
          <nav aria-label="Capitolele ghidului" className="grid gap-6">
            {index.chapters.map((c) => {
              // The chapter of the open article keeps its accent, so the reader
              // sees where they are in a long list (20.10.2026).
              const here = c.articles.some((a) => a.id === current);
              return (
              <div key={c.key}>
                <h2 className={`flex items-center gap-2 border-b border-[#E5E7EB] pb-2 text-[13px] font-bold uppercase tracking-wider ${here ? "text-[#7A4986]" : "text-[#373A45]"}`}
                  data-testid={`guide-chapter-${c.key}`}>
                  <span className={`h-4 w-1 shrink-0 rounded-full ${here ? "bg-[#965EA7]" : "bg-[#965EA7]/40"}`} aria-hidden="true" />
                  <span className="min-w-0">{c.title}</span>
                  <span className="ml-auto shrink-0 rounded-full bg-[#EDEEF0] px-1.5 text-[11px] font-semibold tracking-normal text-[#6B7280]"
                    aria-label={`${c.articles.length} articole`}>{c.articles.length}</span>
                </h2>
                <ul className="mt-2 grid gap-0.5">
                  {c.articles.map((a) => (
                    <li key={a.id}>
                      <button type="button" onClick={() => open(a.id)} aria-current={a.id === current ? "page" : undefined}
                        className={`w-full rounded-lg px-2.5 py-1.5 text-left text-sm leading-snug ${a.id === current ? "bg-[#373A45] text-white" : "text-[#373A45] hover:bg-white"}`}
                        data-testid={`guide-link-${a.id}`}>
                        {a.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              );
            })}
          </nav>
        )}
      </aside>

      <main className="min-w-0 rounded-2xl border border-[#E5E7EB] bg-white p-5 sm:p-8">
        {state === "loading" && <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin" /> Se deschide articolul…</div>}
        {state === "missing" && (
          <p className="text-sm text-[#6B7280]">Articolul acesta nu mai există sau nu e pentru tine. Alege altul din listă.</p>
        )}
        {state === "ready" && <div className="max-w-[68ch]"><GuideArticle article={article} anchor={anchor} onOpen={open} footer={footer?.(article)} /></div>}
        {state === "idle" && (
          <div className="max-w-[68ch]">
            {welcome}
            <News items={index.news} onOpen={open} />
            {footer?.(null)}
          </div>
        )}
      </main>
    </div>
  );
}

function SearchResults({ results, query, onOpen }) {
  if (!results.length) {
    return (
      <p className="rounded-xl bg-white px-3 py-3 text-sm text-[#6B7280]" data-testid="guide-no-results">
        Nimic pentru „{query.trim()}”. Încearcă alt cuvânt — sau spune-ne ce căutai, ca să scriem articolul.
      </p>
    );
  }
  return (
    <ul className="grid gap-1.5" data-testid="guide-results">
      {results.map((r) => (
        <li key={r.section_id}>
          <button type="button" onClick={() => onOpen(r.article_id, r.anchor)}
            className="w-full rounded-xl bg-white px-3 py-2.5 text-left hover:ring-1 hover:ring-[#965EA7]/40">
            <span className="block text-sm font-semibold text-[#373A45]">{r.title}</span>
            {r.heading && <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-[#7A4986]"><ChevronRight size={12} aria-hidden="true" />{r.heading}</span>}
            <span className="mt-1 block text-xs leading-relaxed text-[#6B7280]">{r.snippet}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function News({ items, onOpen }) {
  if (!items?.length) return null;
  return (
    <section className="mt-6" data-testid="guide-news">
      <h2 className="flex items-center gap-1.5 font-display text-lg font-bold text-[#373A45]">
        <Sparkles size={16} className="text-[#965EA7]" aria-hidden="true" /> Ce e nou
      </h2>
      <ul className="mt-3 grid gap-2">
        {items.map((n) => (
          <li key={n.id}>
            <button type="button" onClick={() => onOpen(n.id)} className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-left hover:border-[#965EA7]/40">
              <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-sm font-semibold text-[#373A45]">{n.title}</span>
                <span className="text-xs tabular-nums text-[#9CA3AF]">{longDay(n.updated)}</span>
              </span>
              <span className="mt-1 block text-sm text-[#6B7280]">{n.change || n.summary}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
